/**
 * SubscribersList - Paginated list of accounts on a specific plan
 *
 * Features:
 * - Account cards with status and user count
 * - Pagination controls
 * - Loading skeleton state
 * - Empty state
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton } from '@respectly/ui';
import {
  Building2,
  Users,
  ChevronRight,
  ChevronLeft,
  UserPlus,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { AccountStatusBadge, BillingStatusBadge } from '../accounts';
import type { AccountListItem, PaginationMeta } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface SubscribersListProps {
  planId: string;
  planName?: string;
  subscribers: AccountListItem[];
  pagination: PaginationMeta | null;
  isLoading?: boolean;
  onViewAccount?: (id: string) => void;
  onPageChange?: (page: number) => void;
  className?: string;
}

// ============================================
// List Item
// ============================================

interface ListItemProps {
  account: AccountListItem;
  onView?: () => void;
  index: number;
}

function SubscriberListItem({ account, onView, index }: ListItemProps) {
  const createdAgo = formatDistanceToNow(new Date(account.createdAt), {
    addSuffix: true,
  });

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg',
        'bg-white dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60',
        'hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer'
      )}
      onClick={onView}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Icon */}
        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        </div>

        {/* Account info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {account.name}
            </p>
            <AccountStatusBadge status={account.status} size="sm" />
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <Users className="h-3 w-3" />
              {account.userCount} user{account.userCount !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-slate-400">
              Joined {createdAgo}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <BillingStatusBadge status={account.billingStatus} size="sm" />
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </div>
    </motion.div>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState({ planName }: { planName?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
        <UserPlus className="h-7 w-7 text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">
        No subscribers yet
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        {planName
          ? `No accounts are currently subscribed to the ${planName} plan.`
          : 'No accounts are currently subscribed to this plan.'}
      </p>
    </div>
  );
}

// ============================================
// Pagination
// ============================================

interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
}

function Pagination({ pagination, onPageChange }: PaginationProps) {
  const { page, totalPages, total, limit } = pagination;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
      <p className="text-sm text-slate-500">
        Showing {startItem}-{endItem} of {total} accounts
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-slate-600 dark:text-slate-400 tabular-nums">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function SubscribersList({
  planId: _planId,
  planName,
  subscribers,
  pagination,
  isLoading,
  onViewAccount,
  onPageChange,
  className,
}: SubscribersListProps) {
  if (isLoading) {
    return <SubscribersListSkeleton className={className} />;
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">
            Subscribers
            {pagination && pagination.total > 0 && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({pagination.total})
              </span>
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {subscribers.length === 0 ? (
          <EmptyState planName={planName} />
        ) : (
          <div className="space-y-2">
            {subscribers.map((account, index) => (
              <SubscriberListItem
                key={account.id}
                account={account}
                onView={() => onViewAccount?.(account.id)}
                index={index}
              />
            ))}

            {pagination && pagination.totalPages > 1 && onPageChange && (
              <Pagination pagination={pagination} onPageChange={onPageChange} />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function SubscribersListSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3 rounded-lg border border-slate-200/60 dark:border-slate-700/60"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
